/**
 * @ComponentFor ProductListBlockViewModel
 */
import React, { useEffect, useRef, useState } from 'react';
import { styled, StyledProps } from '@glitz/react';
import ProductListBlockViewModelType from './ProductListBlockViewModel.type';
import connect from 'Shared/connect';
import { genericImpressions } from 'TrackingInformation';
import { isCompact } from 'Shared/Viewport';
import ProductCardList from 'Product/SwipeableList';
import RotatedHeader from 'Shared/RotatedHeader';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';
import { SwipeLeft, SwipeRight } from 'Shared/SwiperList/SwiperNav';

type RequiredPropType = ProductListBlockViewModelType;

type ConnectedPropType = {
  currentBreakpoint: number;
  trackingListName: string;
  spacing: boolean;
};

type PropType = RequiredPropType & ConnectedPropType & StyledProps;

const SingleWrap = styled(GridWrap, {
  padding: { y: '120px' }
})

const Wrap = styled(GridWrap, {
  padding: { y: '80px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { y: '120px' }
  },
})

const NavWrap = styled.div({
  position: 'relative'
})

const LabelWrap = styled.div({
  justifyContent: 'center',
})

const SwiperWrap = styled.div({
  overflow: 'hidden'
})

export const ProductsBlock = connect(
  state => ({
    currentBreakpoint: state.currentBreakpoint,
    trackingListName: state.currentPage != undefined && state.currentPage.meta != undefined ? state.currentPage.meta.title : '',
  }),
)((props: PropType & { currentBreakpoint: number }) => {
  const { currentBreakpoint, items, label, displaySingleProductOnEachSlide } = props;
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(false);
  const isMobile = isCompact(currentBreakpoint);
  const swiperRef = useRef<any>(null);

  const slideChange = (swiper: any) => {
    if (swiper) {
      setPrevDisabled(false);
      setNextDisabled(false);

      if (swiper.isBeginning) {
        setPrevDisabled(true);
      }

      if (swiper.isEnd) {
        setNextDisabled(true);
      }
    }
  }

  useEffect(() => {
    genericImpressions(props.items, props.trackingListName);
  }, []);

  if (!isMobile && displaySingleProductOnEachSlide) {
    return (
      <SingleWrap className='grid wsb'>
        <NavWrap className={createGridClasses(0, 1, createOffsetGridClassLarge(3))}>
          <SwipeLeft
            isCompact={isMobile}
            color={prevDisabled ? style.colors.monochrome.mediumGrey : style.colors.monochrome.black}
            onClick={() => swiperRef.current.slidePrev()}
          />
        </NavWrap>
        <LabelWrap className={createGridClasses(0, 1, createOffsetGridClassLarge(4))}>
          <RotatedHeader label={label} isCompact={isMobile} />
        </LabelWrap>
        <SwiperWrap className={createGridClasses(0, 4, createOffsetGridClassLarge(5))}>
          <ProductCardList
            products={items}
            numberOfProductsVisible={1}
            cellSpacing={0}
            isCompact={isMobile}
            freeMode={false}
            showNav={false}
            sliderRef={swiperRef}
            onSlideChange={slideChange}
          />
        </SwiperWrap>
        <NavWrap className={createGridClasses(0, 1, createOffsetGridClassLarge(10))}>
          <SwipeRight
            isCompact={isMobile}
            color={nextDisabled ? style.colors.monochrome.mediumGrey : style.colors.monochrome.black}
            onClick={() => swiperRef.current.slideNext()}
          />
        </NavWrap>
      </SingleWrap>
    );
  }

  return (
    <Wrap className='grid wsb'>
      <div className={createGridClasses(12, 11, createOffsetGridClassLarge(2))}>
        <ProductCardList products={items} isCompact={isMobile} alternativeNav={true} showNav={!isMobile} listHeading={label} showSwirl={isMobile} />
      </div>
    </Wrap>
  );
});

export default ProductsBlock;